import React, { useState } from "react";
import {
  withApi,
  withBrowser,
  withNotifier,
  withIntl,
  apiShape,
  browserShape,
  notifierShape,
  intlShape,
  ActionBar,
  Container,
  Heading,
  Loader,
  isSomething,
} from "lcm-iot-commons";
import UserEmailEditForm from "./UserEmailEditForm";
import UserContactDetailsForm from "./UserContactDetailsForm";
import { getTimeZone } from "../../utils/timeZones";
import { getLocaleOption } from "../../utils/locales";

type Props = {
  api: apiShape;
  browser: browserShape;
  notifier: notifierShape;
  intl: intlShape;
};

export function UserProfileEdit({ api, browser, notifier, intl }: Props) {
  const [userDetails, setUserDetails] = useState();
  const [isLoading, setIsLoading] = useState(true);

  const formUser = (user: $TSFixMe) => {
    if (!user) return user;

    const formUserObj = {
      id: user.id,
      email: user.email,
      firstName: user.first_name,
      lastName: user.last_name,
      ssoProvider: user.sso_provider,
      timeZone: getTimeZone(user.time_zone),
      locale: getLocaleOption(user.locale),
    };
    if (user.address) {
      (formUserObj as $TSFixMe).company = user.address.company_name;
      (formUserObj as $TSFixMe).streetName = user.address.street;
      (formUserObj as $TSFixMe).streetNumber = user.address.street_number;
      (formUserObj as $TSFixMe).zipCode = user.address.zip_code;
      (formUserObj as $TSFixMe).city = user.address.city;
      (formUserObj as $TSFixMe).regionCode = user.address.region_code;
      (formUserObj as $TSFixMe).billingEmail = user.address.email;
      (formUserObj as $TSFixMe).country = {
        name: user.address.country_name,
        code: user.address.country_code,
      };
      (formUserObj as $TSFixMe).phone = user.address.phone;
      (formUserObj as $TSFixMe).vatNumber = user.address.vat_number;
    } else {
      (formUserObj as $TSFixMe).country = {
        name: undefined,
        code: undefined,
      };
    }
    return formUserObj;
  };

  const loadUserDetails = async () => {
    try {
      const user = await api.get("/users/current?include=userroles,demo_data");
      setUserDetails(formUser(user));
    } catch (error) {
      notifier.showError(api.translateError(error));
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    loadUserDetails();
  }, []);

  const isSsoUser = isSomething((userDetails as $TSFixMe)?.ssoProvider);

  const handleSubmit = async (values: $TSFixMe, actions: $TSFixMe) => {
    try {
      const patchUserDetails = {
        first_name: values.firstName,
        last_name: values.lastName,
        time_zone: values.timeZone?.label ? values.timeZone.identifier : null,
        locale: values.locale ? values.locale.identifier : null,
        address: {
          company_name: values.company,
          street: values.streetName,
          street_number: values.streetNumber,
          zip_code: values.zipCode,
          city: values.city,
          region_code: values.regionCode,
          country_code: values.country?.code,
          phone: values.phone,
          vat_number: values.vatNumber,
          email: values.billingEmail,
        },
      };
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      await api.patch(`/users/${userDetails.id}`, patchUserDetails);
      notifier.showSuccess(
        intl.formatMessage({ id: "edit_contact_details.success_notification" }),
      );
      actions.resetForm({ values });
      browser.navigateTo("/profile");
    } catch (error) {
      if (
        (error as $TSFixMe)?.errors?.some(
          (e: $TSFixMe) => e.type === "vat_number",
        )
      ) {
        actions.setErrors({
          vatNumber: intl.formatMessage({
            id: "api.error.tax_address.invalid_input",
          }),
        });
        return;
      }
      notifier.showError(api.translateError(error));
    } finally {
      actions.setSubmitting(false);
    }
  };

  const handleOnSaveClick = async (values: $TSFixMe, actions: $TSFixMe) => {
    await handleSubmit(values, actions);
  };

  const renderContent = isLoading ? null : (
    <>
      <div id="user-profile-edit-contact-details-group">
        <Heading
          level={2}
          id="user-profile-edit-contact-details-header"
          title={intl.formatMessage({ id: "edit_contact_details.header" })}
        />
        <UserContactDetailsForm
          // @ts-expect-error TS(2322) FIXME: Type '{ id: string; initialValues: undefined; ssoU... Remove this comment to see the full error message
          id="user-profile-contact-details-form"
          initialValues={userDetails}
          ssoUser={isSsoUser}
          onSubmit={handleOnSaveClick}
        />
      </div>
      {isSsoUser ? null : (
        <div
          id="user-profile-email-edit-group"
          data-testid="user-profile-email-edit-group"
        >
          <Heading
            level={2}
            id="change-current-email-header"
            title={intl.formatMessage({ id: "change_current_email.header" })}
          />
          {/* @ts-expect-error TS(2322) FIXME: Type '{ id: string; }' is not assignable to type '... Remove this comment to see the full error message */}
          <UserEmailEditForm id="change-current-email-form" />
        </div>
      )}
    </>
  );

  return (
    <Container>
      <ActionBar>
        <Heading
          id="user-profile-edit-header"
          title={intl.formatMessage({ id: "user_profile_edit.header" })}
        />
      </ActionBar>
      {renderContent}
      <Loader loading={isLoading} />
    </Container>
  );
}

export default withApi(withNotifier(withIntl(withBrowser(UserProfileEdit))));
